<template>
  <div class="sprints-home-item">
    <SortHeaderTitle
      :title="headerText"
      :button-text="buttonText"
      :icon-right-position="iconRightPosition"
      :icon-name="buttonIcon"
      :show-icon="showIcon"
      class="-mb-2"
      @click="clickHandler"
    />
    <div
      v-if="!sprintIds.length"
      class="text-deEmphasised-text flex text-sm"
    >
      {{ emptyStateText }}
    </div>
    <div
      v-for="(sprintId, index) in sprintIds"
      :key="sprintId"
    >
      <ListOfBoardsItem
        :item-id="sprintId"
        :board-index="index"
        :item-type="'sprint'"
        :preview-project-boards="previewProjectBoards"
      />
      <div
        v-if="sprintsCoolDownPeriod && index < sprintIds.length - 1"
        class="cooldown-period"
        :class="{ 'active-cooldown': isCoolDownPeriodActive(sprintId, index) }"
      >
        <span class="">{{ translate('coolDown') }}</span>
        <span>{{ getCooldownPeriodDate(sprintId, index) }}</span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import ListOfBoardsItem from '@/components/widgets/ListOfBoardsItem.vue';
import { Sprint } from '@superthread-com/api';
import { convertTimestampToDateSecondFormat } from '@/utilities/timeDifference';
import useTranslate from '@/utilities/composables/useTranslate';
import SortHeaderTitle from '@/components/widgets/SortHeaderTitle';

const { translate } = useTranslate();

const props = defineProps<{
  sprintIds: string[];
  sprintsCoolDownPeriod: number;
  spaceSprints: Sprint[];
  headerText: string;
  buttonText?: string;
  buttonIcon?: string;
  iconRightPosition?: boolean;
  showIcon?: boolean;
  previewProjectBoards?: boolean;
  emptyStateText?: string;
}>();

const emit = defineEmits(['clickHandler']);

const getCooldDownDates = (sprintId: string, index: number) => {
  const sprint = props.spaceSprints.find((sprint) => sprint.id === sprintId);
  const nextSprint = props.spaceSprints[index + 1];

  if (!nextSprint || !sprint || !sprint.end_date) return { startDate: null, endDate: null };

  const startDate = new Date(nextSprint.end_date * 1000);
  const endDate = new Date(sprint.start_date * 1000);

  return {
    startDate: startDate.setDate(startDate.getDate() + 1),
    endDate: endDate.setDate(endDate.getDate() - 1),
  };
};

const getCooldownPeriodDate = (sprintId: string, index: number): string => {
  const { startDate, endDate } = getCooldDownDates(sprintId, index);

  if (!startDate || !endDate) return '';

  return `${convertTimestampToDateSecondFormat(
    startDate / 1000
  )} - ${convertTimestampToDateSecondFormat(endDate / 1000)}`;
};

const isCoolDownPeriodActive = (sprintId: string, index: number): boolean => {
  const { startDate, endDate } = getCooldDownDates(sprintId, index);
  if (!startDate || !endDate) return false;

  const currentDate = new Date();

  return currentDate >= startDate && currentDate <= endDate;
};

const clickHandler = () => {
  emit('clickHandler');
};
</script>
<style lang="scss">
.sprints-home-item {
  @apply flex flex-col gap-y-4;
  .cooldown-period {
    @apply flex items-center justify-between rounded-lg;
    @apply text-text-default text-sm font-normal;
    @apply border border-solid border-standard-border;
    @apply px-[15px] py-[7px] mt-4 bg-standard-bg;

    &.active-cooldown {
      @apply border-2 border-solid border-highlight-default;
    }
  }
}
</style>
